import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Typography,
  CardContent,
  Avatar,
  Chip,
  Grid,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Fab,
  CircularProgress,
  Stack,
  Paper,
  InputAdornment,
  Switch,
  FormControlLabel,
  Card,
  Container,
  Drawer,
  Tooltip,
} from '@mui/material';
import {
  Email,
  LocationOn,
  Edit,
  CameraAlt,

  Badge,

  Save,
  Cancel,
  Verified,
  Add,
  Delete,
  WorkOutline,
  CloudUpload,
  AttachFile,
  LinkedIn,
  Facebook,
  Twitter,
  Instagram,
  Language,
  GitHub,
  YouTube,
  OpenInNew,
  Visibility,
  Close,
  // Download,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { useAuth } from '@/hooks';
import { authService } from '@/services/auth/authService';
import { designationsService } from '@/services/designations/designationsService';
import { uploadService } from '@/services/upload/uploadService';
import { AppDispatch, fetchProfile, setUser } from '@/store';
import type { Designation } from '@/constants/types';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { CanvasPdfViewer } from '@/components/dashboard/feed/CanvasPdfViewer';
import { SlateRichTextEditor } from '@/components/dashboard/SlateRichTextEditor';
import { ContentWithReadMore } from '@/components/dashboard/feed/ContentWithReadMore';
import { Descendant, Text as SlateText, Element as SlateElement } from 'slate';

// Helper function to convert HTML to Slate.js JSON
const deserializeHtmlToSlate = (html: string): string => {
  try {
    // Check if it's already Slate JSON
    const parsed = JSON.parse(html);
    if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].type) {
      // Already Slate JSON, return as is
      return html;
    }
  } catch {
    // Not JSON, proceed with HTML parsing
  }

  const document = new DOMParser().parseFromString(html, 'text/html');
  const nodes = Array.from(document.body.childNodes).map(node => deserializeNode(node)).flat();

  // If empty, return empty paragraph
  if (nodes.length === 0) {
    return JSON.stringify([{ type: 'paragraph', children: [{ text: '' }] }]);
  }

  // Check if all nodes are text nodes (plain text without HTML elements)
  // In Slate, text nodes must be wrapped in block elements
  const hasOnlyTextNodes = nodes.every(node => node.hasOwnProperty('text') && !node.hasOwnProperty('type'));

  if (hasOnlyTextNodes) {
    // Wrap plain text nodes in a paragraph
    return JSON.stringify([{ type: 'paragraph', children: nodes }]);
  }

  return JSON.stringify(nodes);
};

const deserializeNode = (node: Node): any => {
  if (node.nodeType === Node.TEXT_NODE) {
    return { text: node.textContent || '' };
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return { text: '' };
  }

  const element = node as HTMLElement;
  const children = Array.from(element.childNodes).map(deserializeNode).flat();

  // Handle inline formatting
  if (element.nodeName === 'STRONG' || element.nodeName === 'B') {
    return children.map(child => {
      if (child.hasOwnProperty('text') && !child.hasOwnProperty('type')) {
        return { ...child, bold: true };
      }
      return child;
    });
  }
  if (element.nodeName === 'EM' || element.nodeName === 'I') {
    return children.map(child => {
      if (child.hasOwnProperty('text') && !child.hasOwnProperty('type')) {
        return { ...child, italic: true };
      }
      return child;
    });
  }
  if (element.nodeName === 'U') {
    return children.map(child => {
      if (child.hasOwnProperty('text') && !child.hasOwnProperty('type')) {
        return { ...child, underline: true };
      }
      return child;
    });
  }
  if (element.nodeName === 'S' || element.nodeName === 'DEL' || element.nodeName === 'STRIKE') {
    return children.map(child => {
      if (child.hasOwnProperty('text') && !child.hasOwnProperty('type')) {
        return { ...child, strikethrough: true };
      }
      return child;
    });
  }
  if (element.nodeName === 'CODE') {
    return children.map(child => {
      if (child.hasOwnProperty('text') && !child.hasOwnProperty('type')) {
        return { ...child, code: true };
      }
      return child;
    });
  }

  // Handle block elements
  const align = element.style.textAlign || undefined;

  switch (element.nodeName) {
    case 'P':
      return { type: 'paragraph', align, children: children.length > 0 ? children : [{ text: '' }] };
    case 'H1':
      return { type: 'heading-one', align, children: children.length > 0 ? children : [{ text: '' }] };
    case 'H2':
      return { type: 'heading-two', align, children: children.length > 0 ? children : [{ text: '' }] };
    case 'H3':
      return { type: 'heading-three', align, children: children.length > 0 ? children : [{ text: '' }] };
    case 'H4':
      return { type: 'heading-four', align, children: children.length > 0 ? children : [{ text: '' }] };
    case 'H5':
      return { type: 'heading-five', align, children: children.length > 0 ? children : [{ text: '' }] };
    case 'H6':
      return { type: 'heading-six', align, children: children.length > 0 ? children : [{ text: '' }] };
    case 'BLOCKQUOTE':
      return { type: 'block-quote', align, children: children.length > 0 ? children : [{ text: '' }] };
    case 'UL':
      return { type: 'bulleted-list', children: children.length > 0 ? children : [{ text: '' }] };
    case 'OL':
      return { type: 'numbered-list', children: children.length > 0 ? children : [{ text: '' }] };
    case 'LI':
      return { type: 'list-item', children: children.length > 0 ? children : [{ text: '' }] };
    case 'A':
      return { type: 'link', url: element.getAttribute('href') || '', children: children.length > 0 ? children : [{ text: '' }] };
    default:
      return children.length > 0 ? children : { text: '' };
  }
};

// Helper function to convert Slate.js to HTML
const serializeSlateToHtml = (nodes: Descendant[]): string => {
  return nodes.map((node) => serializeNode(node)).join('');
};

// Helper function to convert Slate JSON string to HTML
const convertSlateJsonToHtml = (slateJson: string): string => {
  try {
    const slateNodes = JSON.parse(slateJson) as Descendant[];
    return serializeSlateToHtml(slateNodes);
  } catch {
    // Fallback to plain text if parsing fails
    return slateJson;
  }
};

// Helper function to get HTML for display (handles both HTML and Slate JSON)
export const getHtmlForDisplay = (content: string): string => {
  if (!content) return '';

  try {
    // Check if it's Slate JSON
    const parsed = JSON.parse(content);
    if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].type) {
      // It's Slate JSON, convert to HTML
      return serializeSlateToHtml(parsed);
    }
  } catch {
    // Not JSON, assume it's already HTML
  }

  // Return as-is (it's HTML)
  return content;
};

const serializeNode = (node: Descendant): string => {
  if (SlateText.isText(node)) {
    let text = node.text;
    if ((node as any).code) text = `<code>${text}</code>`;
    if ((node as any).bold) text = `<strong>${text}</strong>`;
    if ((node as any).italic) text = `<em>${text}</em>`;
    if ((node as any).underline) text = `<u>${text}</u>`;
    if ((node as any).strikethrough) text = `<s>${text}</s>`;
    return text;
  }

  if (SlateElement.isElement(node)) {
    const children = node.children.map((n) => serializeNode(n)).join('');
    const align = (node as any).align;
    const style = align ? ` style="text-align: ${align}"` : '';

    switch (node.type) {
      case 'block-quote':
        return `<blockquote${style}>${children}</blockquote>`;
      case 'heading-one':
        return `<h1${style}>${children}</h1>`;
      case 'heading-two':
        return `<h2${style}>${children}</h2>`;
      case 'heading-three':
        return `<h3${style}>${children}</h3>`;
      case 'heading-four':
        return `<h4${style}>${children}</h4>`;
      case 'heading-five':
        return `<h5${style}>${children}</h5>`;
      case 'heading-six':
        return `<h6${style}>${children}</h6>`;
      case 'bulleted-list':
        return `<ul>${children}</ul>`;
      case 'numbered-list':
        return `<ol>${children}</ol>`;
      case 'list-item':
        return `<li>${children}</li>`;
      case 'link':
        return `<a href="${(node as any).url}" target="_blank" rel="noopener noreferrer">${children}</a>`;
      case 'paragraph':
      default:
        return `<p${style}>${children}</p>`;
    }
  }

  return '';
};

// const isDocumentType = (item: any) => {
//   const mimeType = item.mimeType || item.contentType || '';
//   return mimeType === 'application/pdf' ||
//          mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
//          mimeType === 'application/msword' ||
//          mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
//          mimeType === 'application/vnd.ms-powerpoint';
// };

// Helper function to get plain text from Slate nodes
const getPlainTextFromSlate = (nodes: Descendant[]): string => {
  return nodes
    .map((node) => {
      if (SlateText.isText(node)) {
        return node.text;
      }
      if (SlateElement.isElement(node) && node.children) {
        return getPlainTextFromSlate(node.children);
      }
      return '';
    })
    .join('');
};

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required').min(2, 'Name must be at least 2 characters'),
  whatsappNumber: Yup.string().required('WhatsApp number is required').matches(/^[0-9]{10}$/, 'WhatsApp number must be 10 digits'),
  dateOfBirth: Yup.date().required('Date of birth is required').max(new Date(), 'Date of birth cannot be in the future'),
  address: Yup.string().required('Address is required').max(200, 'Address must be less than 200 characters'),
  city: Yup.string().required('City is required').min(2, 'City must be at least 2 characters'),
  state: Yup.string().required('State is required').min(2, 'State must be at least 2 characters'),
  pincode: Yup.string().required('Pincode is required').matches(/^[0-9]{6}$/, 'Pincode must be 6 digits'),
  designation: Yup.string().required('Designation is required'),
  gender: Yup.string().required('Gender is required').oneOf(['Male', 'Female', 'Other'], 'Please select a valid gender'),
  aboutMe: Yup.string().required('About me is required').min(10, 'About me must be at least 10 characters').max(500, 'About me must be less than 500 characters'),
  currentCompanyInfo: Yup.object().when(['isLookingForJob', 'jobPreferences.experienceLevel'], {
    is: (isLookingForJob: boolean, experienceLevel: string) => {
      // Make optional if looking for job and experience level is entry
      return isLookingForJob && experienceLevel === 'entry';
    },
    then: (schema) => schema.shape({
      companyname: Yup.string().optional().max(100, 'Company name must be less than 100 characters'),
      companyUrl: Yup.string().optional().url('Please enter a valid URL'),
      addressLine1: Yup.string().optional(),
      addressLine2: Yup.string().optional(),
      city: Yup.string().optional(),
      state: Yup.string().optional(),
      pincode: Yup.string().optional().matches(/^[0-9]{6}$/, 'Pincode must be 6 digits'),
    }),
    otherwise: (schema) => schema.shape({
      companyname: Yup.string().required('Company name is required').max(100, 'Company name must be less than 100 characters'),
      companyUrl: Yup.string().optional().url('Please enter a valid URL'),
      addressLine1: Yup.string().required('Address line 1 is required'),
      addressLine2: Yup.string().required('Address line 2 is required'),
      city: Yup.string().required('City is required'),
      state: Yup.string().required('State is required'),
      pincode: Yup.string().required('Pincode is required').matches(/^[0-9]{6}$/, 'Pincode must be 6 digits'),
    }),
  }),
  socialMediaLinks: Yup.array().of(
    Yup.string()
      .test('required-url', 'URL is required', function(value) {
        if (!value || value.trim() === '') {
          return this.createError({ message: 'URL is required' });
        }
        return true;
      })
      .url('Must be a valid URL')
  ).test('unique-urls', 'Duplicate social media links are not allowed', function(links) {
    if (!links) return true;
    
    const nonEmptyLinks = links.filter(link => link && link.trim() !== '');
    const uniqueLinks = new Set(nonEmptyLinks);
    
    if (nonEmptyLinks.length !== uniqueLinks.size) {
      const duplicateIndex = nonEmptyLinks.findIndex((link, index) => 
        nonEmptyLinks.indexOf(link) !== index
      );
      
      return this.createError({ 
        message: 'This URL already exists',
        path: `socialMediaLinks[${duplicateIndex}]`
      });
    }
    
    return true;
  }),
  isLookingForJob: Yup.boolean(),
  jobPreferences: Yup.object().when('isLookingForJob', {
    is: true,
    then: (schema) => schema.shape({
      jobTitle: Yup.string().required('Looking for is required').min(2, 'Looking for must be at least 2 characters').max(100, 'Looking for must be less than 100 characters'),
      jobDescription: Yup.string()
        .required('Roles & Responsibility is required')
        .test('min-content', 'Roles & Responsibility must be at least 10 characters', function(value) {
          if (!value) return false;
          try {
            const slateNodes = JSON.parse(value) as Descendant[];
            const plainText = getPlainTextFromSlate(slateNodes).trim();
            return plainText.length >= 10;
          } catch {
            return value.trim().length >= 10;
          }
        })
        .test('max-content', 'Roles & Responsibility must be less than 1000 characters', function(value) {
          if (!value) return true;
          try {
            const slateNodes = JSON.parse(value) as Descendant[];
            const plainText = getPlainTextFromSlate(slateNodes);
            return plainText.length <= 1000;
          } catch {
            return value.length <= 1000;
          }
        }),
      jobType: Yup.string().required('Job type is required'),
      experienceLevel: Yup.string().required('Experience level is required'),
      currentSalary: Yup.object().shape({
        max: Yup.number().required('Current salary is required').positive('Salary must be positive'),
        currency: Yup.string().default('INR'),
        denomination: Yup.string().default('lakh'),
        period: Yup.string().default('yearly'),
      }),
      expectedSalary: Yup.object().shape({
        max: Yup.number().required('Expected salary is required').positive('Salary must be positive'),
        currency: Yup.string().default('INR'),
        denomination: Yup.string().default('lakh'),
        period: Yup.string().default('yearly'),
      }),
      skills: Yup.array().of(Yup.string()).min(1, 'At least one skill is required'),
      location: Yup.array().of(Yup.string()).min(1, 'At least one preferred location is required'),
      workMode: Yup.string().required('Work mode is required'),
      noticeType: Yup.string().required('Notice type is required'),
    }),
    otherwise: (schema) => schema.optional(),
  }),
});

const getSocialIcon = (url: string) => {
  if (url.includes('linkedin.com')) return <LinkedIn />;
  if (url.includes('facebook.com')) return <Facebook />;
  if (url.includes('twitter.com') || url.includes('x.com')) return <Twitter />;
  if (url.includes('instagram.com')) return <Instagram />;
  if (url.includes('github.com')) return <GitHub />;
  if (url.includes('youtube.com')) return <YouTube />;
  return <Language />;
};

const getSocialPlatform = (url: string) => {
  if (url.includes('linkedin.com')) return 'LinkedIn';
  if (url.includes('facebook.com')) return 'Facebook';
  if (url.includes('twitter.com') || url.includes('x.com')) return 'Twitter/X';
  if (url.includes('instagram.com')) return 'Instagram';
  if (url.includes('github.com')) return 'GitHub';
  if (url.includes('youtube.com')) return 'YouTube';
  return 'Website';
};

export const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const dispatch = useDispatch<AppDispatch>();
 
 
  
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [_error, setError] = useState<string | null>(null);
  const [_success, setSuccess] = useState<string | null>(null);
  const [_designations, setDesignations] = useState<Designation[]>([]);
  const [loadingDesignations, setLoadingDesignations] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [skillInput, setSkillInput] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [uploadedResume, setUploadedResume] = useState<any>(null);
  const [_uploadingResume, setUploadingResume] = useState(false);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [showResumeDrawer, setShowResumeDrawer] = useState(false);
    const [expandedContent, setExpandedContent] = useState<Set<string>>(new Set());
  const formik = useFormik({
    initialValues: {
      name: user?.name || '',
      whatsappNumber: user?.whatsappNumber || '',
      dateOfBirth: user?.dateOfBirth ? user.dateOfBirth.split('T')[0] : '',
      address: user?.address || '',
      city: user?.city || '',
      state: user?.state || '',
      pincode: user?.pincode || '',
      designation: (user?.designation || ''),
      gender: user?.gender || '',
      aboutMe: user?.aboutMe || '',
      currentCompanyInfo: {
        companyname: user?.currentCompanyInfo?.companyname || '',
        companyUrl: user?.currentCompanyInfo?.companyUrl || '',
        addressLine1: user?.currentCompanyInfo?.addressLine1 || '',
        addressLine2: user?.currentCompanyInfo?.addressLine2 || '',
        city: user?.currentCompanyInfo?.city || '',
        state: user?.currentCompanyInfo?.state || '',
        pincode: user?.currentCompanyInfo?.pincode || '',
      },
      socialMediaLinks: user?.socialMediaLinks || [''],
      isLookingForJob: user?.isLookingForJob || false,
      jobPreferences: {
        jobTitle: user?.jobPreferences?.jobTitle || user?.jobTitle || '',
        jobDescription: deserializeHtmlToSlate(user?.jobPreferences?.jobDescription || user?.jobDescription || ''),
        jobType: user?.jobPreferences?.jobType || user?.jobType || '',
        experienceLevel: user?.jobPreferences?.experienceLevel || user?.experienceLevel || '',
        currentSalary: {
          max: user?.jobPreferences?.currentSalary?.max || user?.currentSalary?.max || '',
          currency: user?.jobPreferences?.currentSalary?.currency || user?.currentSalary?.currency || 'INR',
          denomination: user?.jobPreferences?.currentSalary?.denomination || user?.currentSalary?.denomination || 'lakh',
          period: user?.jobPreferences?.currentSalary?.period || user?.currentSalary?.period || 'yearly',
        },
        expectedSalary: {
          max: user?.jobPreferences?.expectedSalary?.max || user?.expectedSalary?.max || '',
          currency: user?.jobPreferences?.expectedSalary?.currency || user?.expectedSalary?.currency || 'INR',
          denomination: user?.jobPreferences?.expectedSalary?.denomination || user?.expectedSalary?.denomination || 'lakh',
          period: user?.jobPreferences?.expectedSalary?.period || user?.expectedSalary?.period || 'yearly',
        },
        skills: user?.jobPreferences?.skills || user?.skills || [],
        location: user?.jobPreferences?.location || user?.location || [],
        workMode: user?.jobPreferences?.workMode || user?.workMode || '',
        noticeType: user?.jobPreferences?.noticeType || user?.noticeType || '',
        resume: user?.jobPreferences?.resume || user?.resume || null,
      },
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      try {
        const filteredSocialLinks = values.socialMediaLinks.filter(link => link.trim() !== '');
        
        const baseProfileData = { 
          name: values.name,
          whatsappNumber: values.whatsappNumber,
          dateOfBirth: values.dateOfBirth,
          address: values.address,
          city: values.city,
          state: values.state,
          pincode: values.pincode,
          designation: values.designation,
          gender: values.gender as 'Male' | 'Female' | 'Other' | undefined,
          aboutMe: values.aboutMe,
          currentCompanyInfo: values.currentCompanyInfo,
          socialMediaLinks: filteredSocialLinks,
          isLookingForJob: values.isLookingForJob,
        };

        let resumeId = values.jobPreferences.resume?.id;
        if (values.isLookingForJob && values.jobPreferences.resume && 'file' in values.jobPreferences.resume) {
          setUploadingResume(true);
          try {
            const folder = 'resumes';
            const resumeWithFile = values.jobPreferences.resume as any;
            const response = await uploadService.uploadSingle(resumeWithFile.file, { folder });
            
            if (response.success && response.file) {
              resumeId = response.file.id;
              const uploadedResumeData = {
                id: response.file.id,
                url: response.file.url,
                originalName: response.file.fileName,
                mimeType: resumeWithFile.mimeType,
                size: resumeWithFile.size,
              };
              setUploadedResume(uploadedResumeData);
            } else {
              throw new Error(response.message || 'Resume upload failed');
            }
          } catch (err: any) {
            console.error('Resume upload failed:', err);
            // toast.error(err.message || 'Failed to upload resume');
               console.log(err);
        if (err.errors && Array.isArray(err.errors)) {
          err.errors.forEach((validationError: { field: string; message: string }) => {
            toast.error(validationError.message);
          });
          setError(err.message || 'Validation failed');
        } else {
          setError(err.message || 'Failed to update profile');
          toast.error(err.message || 'Failed to update profile');
        }
            setUploadingResume(false);
            return;
          } finally {
            setUploadingResume(false);
          }
        }

        const profileData = values.isLookingForJob ? {
          ...baseProfileData,
          jobPreferences: {
            jobTitle: values.jobPreferences.jobTitle,
            jobDescription: convertSlateJsonToHtml(values.jobPreferences.jobDescription),
            jobType: values.jobPreferences.jobType as 'full-time' | 'part-time' | 'contract' | 'freelance' | 'internship' | undefined,
            experienceLevel: values.jobPreferences.experienceLevel as 'entry' | 'junior' | 'mid' | 'senior' | 'manager' | 'head' | 'executive' | undefined,
            workMode: values.jobPreferences.workMode as 'remote' | 'onsite' | 'hybrid' | undefined,
            noticeType: values.jobPreferences.noticeType as 'immediate' | '15-days' | '30-days' | '60-days' | '90-days' | undefined,
            currentSalary: values.jobPreferences.currentSalary.max ? {
              max: Number(values.jobPreferences.currentSalary.max),
              currency: values.jobPreferences.currentSalary.currency,
              denomination: values.jobPreferences.currentSalary.denomination as 'thousand' | 'lakh' | 'crore',
              period: values.jobPreferences.currentSalary.period as 'yearly' | 'monthly',
            } : undefined,
            expectedSalary: values.jobPreferences.expectedSalary.max ? {
              max: Number(values.jobPreferences.expectedSalary.max),
              currency: values.jobPreferences.expectedSalary.currency,
              denomination: values.jobPreferences.expectedSalary.denomination as 'thousand' | 'lakh' | 'crore',
              period: values.jobPreferences.expectedSalary.period as 'yearly' | 'monthly',
            } : undefined,
            skills: values.jobPreferences.skills,
            location: values.jobPreferences.location,
            resumeId: resumeId,
          }
        } : baseProfileData;

        await authService.updateProfile(profileData);
        
        const updatedUser = {
          ...user!,
          ...profileData,
          jobPreferences: values.isLookingForJob ? {
            jobTitle: values.jobPreferences.jobTitle,
            jobDescription: values.jobPreferences.jobDescription,
            jobType: values.jobPreferences.jobType as 'full-time' | 'part-time' | 'contract' | 'freelance' | 'internship' | undefined,
            experienceLevel: values.jobPreferences.experienceLevel as 'entry' | 'junior' | 'mid' | 'senior' | 'manager' | 'head' | 'executive' | undefined,
            currentSalary: values.jobPreferences.currentSalary.max ? {
              max: Number(values.jobPreferences.currentSalary.max),
              currency: values.jobPreferences.currentSalary.currency,
              denomination: values.jobPreferences.currentSalary.denomination as 'thousand' | 'lakh' | 'crore',
              period: values.jobPreferences.currentSalary.period as 'yearly' | 'monthly',
            } : undefined,
            expectedSalary: values.jobPreferences.expectedSalary.max ? {
              max: Number(values.jobPreferences.expectedSalary.max),
              currency: values.jobPreferences.expectedSalary.currency,
              denomination: values.jobPreferences.expectedSalary.denomination as 'thousand' | 'lakh' | 'crore',
              period: values.jobPreferences.expectedSalary.period as 'yearly' | 'monthly',
            } : undefined,
            skills: values.jobPreferences.skills,
            location: values.jobPreferences.location,
            workMode: values.jobPreferences.workMode as 'remote' | 'onsite' | 'hybrid' | undefined,
            noticeType: values.jobPreferences.noticeType as 'immediate' | '15-days' | '30-days' | '60-days' | '90-days' | undefined,
            resumeId: resumeId,
          } : undefined
        };
        dispatch(setUser(updatedUser));
        setSuccess('Profile updated successfully!');
        setEditMode(false);
        setTimeout(() => setSuccess(null), 3000);
        toast.success('Profile updated successfully!');
      } catch (err: any) {
        console.log(err);
        if (err.errors && Array.isArray(err.errors)) {
          err.errors.forEach((validationError: { field: string; message: string }) => {
            toast.error(validationError.message);
          });
          setError(err.message || 'Validation failed');
        } else {
          setError(err.message || 'Failed to update profile');
          toast.error(err.message || 'Failed to update profile');
        }
      } finally {
        dispatch(fetchProfile())
        setLoading(false);
      }
    },
  });

  if (!user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading profile...</Typography>
      </Box>
    );
  }

  const hasEmptyOrInvalidLinks = () => {
    const links = formik.values.socialMediaLinks;
    const errors = formik.errors.socialMediaLinks;
    
    const hasEmpty = links.some(link => !link || link.trim() === '');
    const hasErrors = Boolean(errors);
    
    return hasEmpty || hasErrors;
  };

  const addSocialLink = () => {
    if (!hasEmptyOrInvalidLinks()) {
      formik.setFieldValue('socialMediaLinks', [...formik.values.socialMediaLinks, '']);
    }
  };

  const removeSocialLink = (index: number) => {
    const newLinks = formik.values.socialMediaLinks.filter((_, i) => i !== index);
    formik.setFieldValue('socialMediaLinks', newLinks.length ? newLinks : ['']);
  };

  const handleSocialLinkChange = (index: number, value: string) => {
    const newLinks = [...formik.values.socialMediaLinks];
    newLinks[index] = value;
    formik.setFieldValue('socialMediaLinks', newLinks);
  };

  // Memoize callbacks to prevent unnecessary re-renders
  const handleJobDescriptionChange = useCallback((value: string) => {
    formik.setFieldValue('jobPreferences.jobDescription', value);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleJobDescriptionBlur = useCallback(() => {
    formik.setFieldTouched('jobPreferences.jobDescription', true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddSkill = () => {
    if (skillInput.trim() && !formik.values.jobPreferences.skills.includes(skillInput.trim())) {
      const currentSkills = formik.values.jobPreferences.skills || [];
      if (currentSkills.length >= 10) {
        toast.error('Maximum 10 skills allowed');
        return;
      }
      formik.setFieldValue('jobPreferences.skills', [...currentSkills, skillInput.trim()]);
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    const currentSkills = formik.values.jobPreferences.skills || [];
    formik.setFieldValue('jobPreferences.skills', currentSkills.filter((skill: string) => skill !== skillToRemove));
  };

  const handleAddLocation = () => {
    if (locationInput.trim() && !formik.values.jobPreferences.location.includes(locationInput.trim())) {
      const currentLocations = formik.values.jobPreferences.location || [];
      if (currentLocations.length >= 10) {
        toast.error('Maximum 10 locations allowed');
        return;
      }
      formik.setFieldValue('jobPreferences.location', [...currentLocations, locationInput.trim()]);
      setLocationInput('');
    }
  };

  const handleRemoveLocation = (locationToRemove: string) => {
    const currentLocations = formik.values.jobPreferences.location || [];
    formik.setFieldValue('jobPreferences.location', currentLocations.filter((loc: string) => loc !== locationToRemove));
  };

  const handleResumeUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a PDF or DOCX file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size should be less than 5MB');
      return;
    }

    const resumeData = {
      file: file,
      originalName: file.name,
      mimeType: file.type,
      size: file.size,
    };
    
    setUploadedResume(resumeData);
    formik.setFieldValue('jobPreferences.resume', resumeData);
    toast.success('Resume selected! Click "Save Changes" to upload and save to your profile.');
    
    event.target.value = '';
  };

  const handleRemoveResume = async () => {
    try {
      const resumeToDelete = uploadedResume || user?.jobPreferences?.resume || user?.resume;
      const resumeId = resumeToDelete?.id;

      if (resumeId) {
        await uploadService.deleteSingleFile(resumeId);
      }

      const params = {
        jobPreferences: {
          resumeId: ""
        }
      };
      await authService.removeUserProfile(params);

      setUploadedResume(null);
      formik.setFieldValue('jobPreferences.resume', null);
      
      toast.success('Resume removed successfully!');
      dispatch(fetchProfile())

    } catch (error: any) {
      console.error('Failed to remove resume:', error);
      toast.error(error.message || 'Failed to remove resume');
    }
  };

  useEffect(() => {
    const fetchDesignations = async () => {
      try {
        setLoadingDesignations(true);
        const response = await designationsService.getDesignations();
        if (response.success && response.data.designations) {
          setDesignations(response.data.designations);
        }
      } catch (error: any) {
        console.error('Failed to fetch designations:', error);
        toast.error('Failed to load designations');
      } finally {
        setLoadingDesignations(false);
      }
    };

    fetchDesignations();
  }, []);

  useEffect(() => {
    if (user?.jobPreferences?.resume || user?.resume) {
      const resumeData = user.jobPreferences?.resume || user.resume;
      setUploadedResume(resumeData);
    }
  }, [user]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }

    setUploading(true);
    setError(null);
    
    try {
      const existingImageID = user?.profilePicture?.id;
      const uploadResponse = await uploadService.uploadSingle(file);
      
      if(uploadResponse.success && uploadResponse.file.url){
        await authService.updateProfilePicture(uploadResponse.file.id)
        if(existingImageID){
          await uploadService.deleteSingleFile(existingImageID)
        }
        setSuccess('Profile picture updated successfully!');
        setTimeout(() => setSuccess(null), 3000);

      } else {
        throw new Error(uploadResponse.message || 'Upload failed');
      }
      
      const uploadedRes = uploadResponse.file;
      const files= {
        id:uploadedRes.id,
        url:uploadedRes.url,
        originalName:uploadedRes.fileName
      }
      dispatch(setUser({ ...user!, profilePicture: files }));
      toast.success('Profile picture updated successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to upload profile picture');
      toast.error(err.message || 'Failed to upload profile picture');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* LinkedIn-style Header with Cover Photo */}
      <Paper 
        elevation={1} 
        sx={{ 
          borderRadius: 4, 
          overflow: 'hidden',
          mb: 3,
          position: 'relative'
        }}
      >
        {/* Cover Photo Section */}
        <Box
          sx={{
            height: { xs: 180, sm: 200, md: 240 },
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%)',
            backgroundImage: 'url(https://s3.hrtalky.com/hr-api-files/1764846884795-498a30cdf23c8915-9.png?w=1200&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              // background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.7) 0%, rgba(118, 75, 162, 0.7) 100%)',
              zIndex: 1,
            },
          }}
        >
       
          {/* <Typography 
            variant="h4" 
            sx={{ 
              color: 'white', 
              fontWeight: 'bold',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              position: 'relative',
              zIndex: 2,
            }}
          >
           Your HR Journey Starts Here.
Learn, Connect & Grow with HRTalky.

          </Typography> */}
          
          {/* Edit Cover Button */}
          {/* <IconButton
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              zIndex: 3,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.3)',
              },
            }}
            onClick={() => coverInputRef.current?.click()}
          >
            <CameraAlt />
          </IconButton> */}
          
          <input
            ref={coverInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: 'none' }}
          />
        </Box>

        {/* Profile Section */}
        <Box sx={{ px: 4, pb: 3, position: 'relative' }}>
          {/* Profile Picture */}
          <Box 
            sx={{ 
              position: 'absolute',
              top: -70,
              left: 32,
              zIndex: 1
            }}
          >
            <Box position="relative" display="inline-block">
              {user.profilePicture?.url ? (
                <Avatar
                  src={user.profilePicture?.url}
                  sx={{
                        width: 120,
                    height: 120,
                    border: '6px solid white',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    fontSize: '4rem',
                    bgcolor: 'primary.main',
                    border: '6px solid white',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  }}
                >
                  {user.name?.charAt(0).toUpperCase()}
                </Avatar>
              )}
              
              <Fab
                size="small"
                color="primary"
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  boxShadow: 1,
                  height:8,
                  width:36
                }}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? <CircularProgress size={24} /> : <CameraAlt sx={{fontSize:16}}/>}
              </Fab>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </Box>
          </Box>

          {/* Edit Profile Button */}
          <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
            <Button
              variant={editMode ? "outlined" : "contained"}
              startIcon={editMode ? <Cancel /> : <Edit />}
              onClick={() => {
                setEditMode(!editMode);
                if (editMode) {
                  formik.resetForm();
                }
              }}
              sx={{ 
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {editMode ? 'Cancel' : 'Edit Profile'}
            </Button>
          </Box>

          {/* Profile Info */}
          <Box sx={{ pt: 8, pl: 2 ,}}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {user.name}
            </Typography>
            
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {typeof user.designation === 'object' ? user.designation?.name : user.designation}
            </Typography>
<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
         
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 ,}}>
                <Email fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {user.email}
                </Typography>
                {user.isEmailVerified && (
                  <Verified sx={{ color: 'success.main', fontSize: 16 }} />
                )}
              </Box>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationOn fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {[user.city, user.state].filter(Boolean).join(', ') || 'Location not specified'}
                </Typography>
              </Box>
              
              
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                label={user.role}
                color="primary"
                variant="filled"
                icon={<Badge />}
              />
              
              {user.isLookingForJob && (
                <Chip
                  label="Open to work"
                  color="success"
                  variant="outlined"
                  icon={<WorkOutline />}
                />
              )}
              
              <Chip
                label={user.membershipTier?.toUpperCase()}
                color="secondary"
                variant="outlined"
              />
            </Stack>

            {user.aboutMe && (
              <Typography variant="body1" sx={{ mb: 3, maxWidth: '70%' }}>
                {user.aboutMe}
              </Typography>
            )}
            
            {(
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formik.values.isLookingForJob}
                      onChange={(e) => {
                        
        setEditMode(true);

                        formik.setFieldValue('isLookingForJob', e.target.checked)}}
                    />
                  }
                  label="Open to work"
                />
              </Box>
            )}
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid size={{ xs: 12, md: 12 }}>
          <Stack spacing={3}>
            {/* Quick Info */}
            {/* <Card elevation={1} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Quick Info
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <AccountCircle sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                      <Typography variant="caption" color="text.secondary" display="block">
                        Member Since
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Badge sx={{ fontSize: 32, color: 'secondary.main', mb: 1 }} />
                      <Typography variant="caption" color="text.secondary" display="block">
                        Membership
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {user.membershipTier}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Email sx={{ fontSize: 32, color: user.isEmailVerified ? 'success.main' : 'warning.main', mb: 1 }} />
                      <Typography variant="caption" color="text.secondary" display="block">
                        Email Status
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        {user.isEmailVerified ? (
                          <Typography variant="body2" fontWeight="medium" color="success.main">
                            Verified
                          </Typography>
                        ) : (
                          <Typography variant="body2" fontWeight="medium" color="warning.main">
                            Unverified
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <WhatsApp sx={{ fontSize: 32, color: user.isWhatsappVerified ? 'success.main' : 'warning.main', mb: 1 }} />
                      <Typography variant="caption" color="text.secondary" display="block">
                        WhatsApp Status
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        {user.isWhatsappVerified ? (
                          <Typography variant="body2" fontWeight="medium" color="success.main">
                            Verified
                          </Typography>
                        ) : (
                          <Typography variant="body2" fontWeight="medium" color="warning.main">
                            Unverified
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card> */}

            {/* About Section */}
            {/* <Card elevation={1} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  About
                </Typography>
                
                {editMode ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    name="aboutMe"
                    label="About Me"
                    value={formik.values.aboutMe}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.aboutMe && Boolean(formik.errors.aboutMe)}
                    helperText={formik.touched.aboutMe && formik.errors.aboutMe}
                    placeholder="Tell us about yourself, your experience, and what makes you unique..."
                  />
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    {user.aboutMe || 'No description provided'}
                  </Typography>
                )}
              </CardContent>
            </Card> */}


            {/* Career Preferences */}
            {formik.values.isLookingForJob &&(
              <Card elevation={1} sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom mb={2}>
                    Career Preferences
                  </Typography>
                  
                  {editMode ? (
                    <Grid container spacing={3}>
                    {/* Looking For */}
                    <Grid size={{xs: 12, md: 12}}>
                      {editMode ? (
                        <TextField
                          fullWidth
                          size="small"
                          label="Looking for"
                          name="jobPreferences.jobTitle"
                          value={formik.values.jobPreferences.jobTitle}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.jobPreferences?.jobTitle && Boolean(formik.errors.jobPreferences?.jobTitle)}
                          helperText={
                            (formik.touched.jobPreferences?.jobTitle && formik.errors.jobPreferences?.jobTitle) ||
                            `${formik.values.jobPreferences.jobTitle.length}/100 characters (min: 2)`
                          }
                          placeholder="e.g. Senior HR Manager"
                          required
                        />
                      ) : (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            Looking for
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {user?.jobPreferences?.jobTitle || 'Not specified'}
                          </Typography>
                        </>
                      )}
                    </Grid>

                    {/* Job Description */}
                    <Grid size={{ xs: 12 }}>
                      {editMode ? (
                        <SlateRichTextEditor
                          value={formik.values.jobPreferences.jobDescription || ''}
                          onChange={handleJobDescriptionChange}
                          onBlur={handleJobDescriptionBlur}
                          error={formik.touched.jobPreferences?.jobDescription && Boolean(formik.errors.jobPreferences?.jobDescription)}
                          helperText={formik.touched.jobPreferences?.jobDescription ? formik.errors.jobPreferences?.jobDescription as string : undefined}
                          placeholder="Describe your ideal role, responsibilities, and requirements..."
                          maxLength={1000}
                          title="Roles & Responsibility"
                        />
                      ) : (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            Roles & Responsibility
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {user?.jobPreferences?.jobDescription || 'Not specified'}
                          </Typography>
                        </>
                      )}
                    </Grid>

                    {/* Job Type */}
                    <Grid size={{xs: 12, md: 4}}>
                      {editMode ? (
                        <TextField
                          fullWidth
                          select
                          size="small"
                          label="Job Type"
                          name="jobPreferences.jobType"
                          value={formik.values.jobPreferences.jobType}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.jobPreferences?.jobType && Boolean(formik.errors.jobPreferences?.jobType)}
                          helperText={formik.touched.jobPreferences?.jobType && formik.errors.jobPreferences?.jobType}
                          required
                        >
                          <MenuItem value="full-time">Full-time</MenuItem>
                          <MenuItem value="part-time">Part-time</MenuItem>
                          <MenuItem value="contract">Contract</MenuItem>
                          <MenuItem value="freelance">Freelance</MenuItem>
                          <MenuItem value="internship">Internship</MenuItem>
                        </TextField>
                      ) : (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            Job Type
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                            {user?.jobPreferences?.jobType || 'Not specified'}
                          </Typography>
                        </>
                      )}
                    </Grid>

                    {/* Experience Level */}
                    <Grid size={{xs: 12, md: 4}}>
                      {editMode ? (
                        <TextField
                          fullWidth
                          select
                          size="small"
                          label="Experience Level"
                          name="jobPreferences.experienceLevel"
                          value={formik.values.jobPreferences.experienceLevel}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.jobPreferences?.experienceLevel && Boolean(formik.errors.jobPreferences?.experienceLevel)}
                          helperText={formik.touched.jobPreferences?.experienceLevel && formik.errors.jobPreferences?.experienceLevel}
                          required
                        >
                          <MenuItem value="entry">Fresher / Entry Level – 0 to 1 Year</MenuItem>
                          <MenuItem value="junior">Junior Level – 1 to 3 Years</MenuItem>
                          <MenuItem value="mid">Mid-Level – 3 to 5 Years</MenuItem>
                          <MenuItem value="senior">Senior Level – 5 to 8 Years</MenuItem>
                          <MenuItem value="manager">Managerial Level – 8 to 12 Years</MenuItem>
                          <MenuItem value="head">Leadership / Head Level – 12 to 18 Years</MenuItem>
                          <MenuItem value="executive">Director / C-Level – 18+ Years</MenuItem>
                        </TextField>
                      ) : (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            Experience Level
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                            {user?.jobPreferences?.experienceLevel || 'Not specified'}
                          </Typography>
                        </>
                      )}
                    </Grid>

                    {/* Work Mode */}
                    <Grid size={{xs: 12, md: 4}}>
                      {editMode ? (
                        <TextField
                          fullWidth
                          select
                          size="small"
                          label="Work Mode"
                          name="jobPreferences.workMode"
                          value={formik.values.jobPreferences.workMode}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.jobPreferences?.workMode && Boolean(formik.errors.jobPreferences?.workMode)}
                          helperText={formik.touched.jobPreferences?.workMode && formik.errors.jobPreferences?.workMode}
                          required
                        >
                          <MenuItem value="remote">Remote</MenuItem>
                          <MenuItem value="onsite">On-site</MenuItem>
                          <MenuItem value="hybrid">Hybrid</MenuItem>
                        </TextField>
                      ) : (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            Work Mode
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                            {user?.jobPreferences?.workMode || 'Not specified'}
                          </Typography>
                        </>
                      )}
                    </Grid>

                    {/* Notice Type */}
                    <Grid size={{xs: 12, md: 4}}>
                      {editMode ? (
                        <TextField
                          fullWidth
                          select
                          size="small"
                          label="Notice Period"
                          name="jobPreferences.noticeType"required
                          value={formik.values.jobPreferences.noticeType}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.jobPreferences?.noticeType && Boolean(formik.errors.jobPreferences?.noticeType)}
                          helperText={formik.touched.jobPreferences?.noticeType && formik.errors.jobPreferences?.noticeType}
                        >
                          <MenuItem value="immediate">Immediate</MenuItem>
                          <MenuItem value="15-days">15 Days</MenuItem>
                          <MenuItem value="30-days">30 Days</MenuItem>
                          <MenuItem value="60-days">60 Days</MenuItem>
                          <MenuItem value="90-days">90 Days</MenuItem>
                        </TextField>
                      ) : (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            Notice Period
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                            {user?.jobPreferences?.noticeType || 'Not specified'}
                          </Typography>
                        </>
                      )}
                    </Grid>

                    {/* Current Salary */}
                    <Grid size={{ xs: 12 }}>
                          {editMode  &&  <Typography variant="subtitle2" gutterBottom mb={2}>
                            Current Salary
                          </Typography>}
                      {editMode ? (
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 6, md: 3 }}>
                            <TextField
                              fullWidth
                              name="jobPreferences.currentSalary.max"
                              label="Amount"
                              type="number"
                              required
                              value={formik.values.jobPreferences.currentSalary.max}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={formik.touched.jobPreferences?.currentSalary?.max && Boolean(formik.errors.jobPreferences?.currentSalary?.max)}
                              helperText={formik.touched.jobPreferences?.currentSalary?.max && formik.errors.jobPreferences?.currentSalary?.max}
                              size="small"
                              slotProps={{ htmlInput: { min: 0 } }}
                            />
                          </Grid>
                          <Grid size={{ xs: 6, md: 3 }}>
                            <TextField
                              fullWidth
                              select
                              name="jobPreferences.currentSalary.denomination"
                              label="Denomination"
                              
                              value={formik.values.jobPreferences.currentSalary.denomination}
                              onChange={formik.handleChange}
                              size="small"
                            >
                              <MenuItem value="thousand">Thousand</MenuItem>
                              <MenuItem value="lakh">Lakh</MenuItem>
                              <MenuItem value="crore">Crore</MenuItem>
                            </TextField>
                          </Grid>
                          <Grid size={{ xs: 6, md: 3 }}>
                            <TextField
                              fullWidth
                              select
                              name="jobPreferences.currentSalary.period"
                              label="Period"
                              value={formik.values.jobPreferences.currentSalary.period}
                              onChange={formik.handleChange}
                              size="small"
                            >
                              <MenuItem value="yearly">Yearly</MenuItem>
                              <MenuItem value="monthly">Monthly</MenuItem>
                            </TextField>
                          </Grid>
                          <Grid size={{ xs: 6, md: 3 }}>
                            <TextField
                              fullWidth
                              select
                              name="jobPreferences.currentSalary.currency"
                              label="Currency"
                              value={formik.values.jobPreferences.currentSalary.currency}
                              onChange={formik.handleChange}
                              size="small"
                            >
                              <MenuItem value="INR">INR</MenuItem>
                              <MenuItem value="USD">USD</MenuItem>
                              <MenuItem value="EUR">EUR</MenuItem>
                            </TextField>
                          </Grid>
                        </Grid>
                      ) : (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            Current Salary
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {user.jobPreferences?.currentSalary?.max 
                              ? `${user.jobPreferences.currentSalary.max} ${user.jobPreferences.currentSalary.denomination} ${user.jobPreferences.currentSalary.currency} (${user.jobPreferences.currentSalary.period})`
                              : 'Not specified'
                            }
                          </Typography>
                        </>
                      )}
                    </Grid>

                    {/* Expected Salary */}
                    <Grid size={{ xs: 12 }}>
                        {editMode  && <Typography variant="subtitle2" gutterBottom mb={2}>
                            Expected Salary
                          </Typography>}
                      {editMode ? (
                        <Grid container spacing={2}>
                          
                          <Grid size={{ xs: 6, md: 3 }}>
                             
                            <TextField
                              fullWidth
                              name="jobPreferences.expectedSalary.max"
                              label="Amount"
                              required

                              type="number"
                              value={formik.values.jobPreferences.expectedSalary.max}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={formik.touched.jobPreferences?.expectedSalary?.max && Boolean(formik.errors.jobPreferences?.expectedSalary?.max)}
                              helperText={formik.touched.jobPreferences?.expectedSalary?.max && formik.errors.jobPreferences?.expectedSalary?.max}
                              size="small"
                              slotProps={{ htmlInput: { min: 0 } }}
                            />
                          </Grid>
                          <Grid size={{ xs: 6, md: 3 }}>
                            <TextField
                              fullWidth
                              select
                              name="jobPreferences.expectedSalary.denomination"
                              label="Denomination"
                              value={formik.values.jobPreferences.expectedSalary.denomination}
                              onChange={formik.handleChange}
                              size="small"
                            >
                              <MenuItem value="thousand">Thousand</MenuItem>
                              <MenuItem value="lakh">Lakh</MenuItem>
                              <MenuItem value="crore">Crore</MenuItem>
                            </TextField>
                          </Grid>
                          <Grid size={{ xs: 6, md: 3 }}>
                            <TextField
                              fullWidth
                              select
                              name="jobPreferences.expectedSalary.period"
                              label="Period"
                              value={formik.values.jobPreferences.expectedSalary.period}
                              onChange={formik.handleChange}
                              size="small"
                            >
                              <MenuItem value="yearly">Yearly</MenuItem>
                              <MenuItem value="monthly">Monthly</MenuItem>
                            </TextField>
                          </Grid>
                          <Grid size={{ xs: 6, md: 3 }}>
                            <TextField
                              fullWidth
                              select
                              name="jobPreferences.expectedSalary.currency"
                              label="Currency"
                              value={formik.values.jobPreferences.expectedSalary.currency}
                              onChange={formik.handleChange}
                              size="small"
                            >
                              <MenuItem value="INR">INR</MenuItem>
                              <MenuItem value="USD">USD</MenuItem>
                              <MenuItem value="EUR">EUR</MenuItem>
                            </TextField>
                          </Grid>
                        </Grid>
                      ) : (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            Expected Salary
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {user.jobPreferences?.expectedSalary?.max 
                              ? `${user.jobPreferences.expectedSalary.max} ${user.jobPreferences.expectedSalary.denomination} ${user.jobPreferences.expectedSalary.currency} (${user.jobPreferences.expectedSalary.period})`
                              : 'Not specified'
                            }
                          </Typography>
                        </>
                      )}
                    </Grid>

                    {/* Skills */}
                    <Grid size={{ xs: 12 }}>
                      {editMode ? (
                        <Box>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <TextField
                              size="small"
                              label="Add skill"
                              value={skillInput}
                              required
                              onChange={(e) => setSkillInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddSkill();
                                }
                              }}
                              sx={{ flex: 1 }}
                            />
                            <Button
                              variant="outlined"
                              onClick={handleAddSkill}
                              disabled={!skillInput.trim() || formik.values.jobPreferences.skills.length >= 10}
                            >
                              Add
                            </Button>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {formik.values.jobPreferences.skills.map((skill: string, index: number) => (
                              <Chip
                                key={index}
                                label={skill}
                                onDelete={() => handleRemoveSkill(skill)}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                          {formik.touched.jobPreferences?.skills && formik.errors.jobPreferences?.skills && (
                            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                              {formik.errors.jobPreferences.skills}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            Skills
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {(user.jobPreferences?.skills || []).map((skill: string, index: number) => (
                              <Chip
                                key={index}
                                label={skill}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </>
                      )}
                    </Grid>

                    {/* Preferred Locations */}
                    <Grid size={{ xs: 12 }}>
                      {editMode ? (
                        <Box>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <TextField
                              size="small"
                              label="Preferred location"
                              required

                              value={locationInput}
                              onChange={(e) => setLocationInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddLocation();
                                }
                              }}
                              sx={{ flex: 1 }}
                            />
                            <Button
                              variant="outlined"
                              onClick={handleAddLocation}
                              disabled={!locationInput.trim() || formik.values.jobPreferences.location.length >= 10}
                            >
                              Add
                            </Button>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {formik.values.jobPreferences.location.map((location: string, index: number) => (
                              <Chip
                                key={index}
                                label={location}
                                onDelete={() => handleRemoveLocation(location)}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                          {formik.touched.jobPreferences?.location && formik.errors.jobPreferences?.location && (
                            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                              {formik.errors.jobPreferences.location}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            Preferred Locations
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {(user.jobPreferences?.location || []).map((location: string, index: number) => (
                              <Chip
                                key={index}
                                label={location}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </>
                      )}
                    </Grid>

                    {/* Resume */}
                    <Grid size={{ xs: 12 }}>
                      {editMode ? (
                        uploadedResume ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={uploadedResume.originalName}
                              onDelete={handleRemoveResume}
                              color="secondary"
                              icon={<AttachFile />}
                            />
                          </Box>
                        ) : (
                          <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUpload />}
                            sx={{ textTransform: 'none' }}
                          >
                            Upload Resume
                            <input
                              type="file"
                              hidden
                              accept=".pdf,.doc,.docx"
                              onChange={handleResumeUpload}
                            />
                          </Button>
                        )
                      ) : (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            Resume
                          </Typography>
                          {(user?.jobPreferences?.resume || user?.resume) ? (
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: 'background.paper',
                                '&:hover': {
                                  bgcolor: 'action.hover',
                                  borderColor: 'primary.main',
                                }
                              }}
                            >
                              <Stack direction="row" spacing={2} alignItems="center">
                                <AttachFile color="primary" />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight="600" noWrap>
                                    {(user.jobPreferences?.resume?.originalName || user.resume?.originalName) || 'Resume.pdf'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Click to view or download
                                  </Typography>
                                </Box>
                                <Stack direction="row" spacing={1}>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => setShowResumeDrawer(true)}
                                    sx={{
                                      bgcolor: 'primary.main',
                                      color: 'white',
                                      '&:hover': {
                                        bgcolor: 'primary.dark',
                                      },
                                      '&:focus': {
                                        bgcolor: 'primary.main',
                                      },
                                      '&:focus-visible': {
                                        bgcolor: 'primary.main',
                                        outline: '2px solid',
                                        outlineColor: 'primary.light',
                                        outlineOffset: 2,
                                      }
                                    }}
                                  >
                                    <Visibility fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => {
                                      const resumeUrl = user.jobPreferences?.resume?.url || user.resume?.url;
                                      if (resumeUrl) {
                                        window.open(resumeUrl, '_blank');
                                      }
                                    }}
                                    sx={{
                                      bgcolor: 'success.main',
                                      color: 'white',
                                      '&:hover': {
                                        bgcolor: 'success.dark',
                                      },
                                      '&:focus': {
                                        bgcolor: 'success.main',
                                      },
                                      '&:focus-visible': {
                                        bgcolor: 'success.main',
                                        outline: '2px solid',
                                        outlineColor: 'success.light',
                                        outlineOffset: 2,
                                      }
                                    }}
                                  >
                                    <OpenInNew fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </Stack>
                            </Paper>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No resume uploaded
                            </Typography>
                          )}
                        </>
                      )}
                    </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={3}>
                      {/* Looking For */}
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Looking for
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {user?.jobPreferences?.jobTitle || 'Not specified'}
                        </Typography>
                      </Grid>

                      {/* Job Type */}
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Job Type
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {user?.jobPreferences?.jobType || 'Not specified'}
                        </Typography>
                      </Grid>

                      {/* Experience Level */}
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Experience Level
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {user?.jobPreferences?.experienceLevel || 'Not specified'}
                        </Typography>
                      </Grid>

                      {/* Work Mode */}
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Work Mode
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {user?.jobPreferences?.workMode || 'Not specified'}
                        </Typography>
                      </Grid>

                      {/* Notice Period */}
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Notice Period
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {user?.jobPreferences?.noticeType || 'Not specified'}
                        </Typography>
                      </Grid>

                      {/* Current Salary */}
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Current Salary
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {user.jobPreferences?.currentSalary?.max 
                            ? `${user.jobPreferences.currentSalary.max} ${user.jobPreferences.currentSalary.denomination} ${user.jobPreferences.currentSalary.currency} (${user.jobPreferences.currentSalary.period})`
                            : 'Not specified'
                          }
                        </Typography>
                      </Grid>

                      {/* Expected Salary */}
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Expected Salary
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {user.jobPreferences?.expectedSalary?.max 
                            ? `${user.jobPreferences.expectedSalary.max} ${user.jobPreferences.expectedSalary.denomination} ${user.jobPreferences.expectedSalary.currency} (${user.jobPreferences.expectedSalary.period})`
                            : 'Not specified'
                          }
                        </Typography>
                      </Grid>

                   

                      {/* Skills */}
                      <Grid size={{ xs: 12, md: 4  }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Skills
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {(user.jobPreferences?.skills || []).slice(0, 2).map((skill: string, index: number) => (
                            <Chip
                              key={index}
                              label={skill}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                          {(user.jobPreferences?.skills || []).length > 2 && (
                            <Tooltip
                              title={
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  {(user.jobPreferences?.skills || []).slice(2).map((skill: string, index: number) => (
                                    <Typography key={index} variant="body2" sx={{ color: 'white' }}>
                                      {skill}
                                    </Typography>
                                  ))}
                                </Box>
                              }
                            >
                              <Chip
                                label={`+${(user.jobPreferences?.skills || []).length - 2} Skills`}
                                size="small"
                                color="primary"
                                variant="filled"
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </Grid>

                      {/* Preferred Locations */}
                      <Grid size={{ xs: 12, md: 4  }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Preferred Locations
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {(user.jobPreferences?.location || []).slice(0, 2).map((location: string, index: number) => (
                            <Chip
                              key={index}
                              label={location}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          ))}
                          {(user.jobPreferences?.location || []).length > 2 && (
                            <Tooltip
                              title={
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  {(user.jobPreferences?.location || []).slice(2).map((location: string, index: number) => (
                                    <Typography key={index} variant="body2" sx={{ color: 'white' }}>
                                      {location}
                                    </Typography>
                                  ))}
                                </Box>
                              }
                            >
                              <Chip
                                label={`+${(user.jobPreferences?.location || []).length - 2} Locations`}
                                size="small"
                                color="secondary"
                                variant="filled"
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </Grid>
   {/* Job Description */}
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Roles & Responsibility
                        </Typography>
                        {user?.jobPreferences?.jobDescription ? (
                          <ContentWithReadMore
                            content={getHtmlForDisplay(user.jobPreferences.jobDescription)}
                            feedId="profile-job-description-main"
                            expandedContent={expandedContent}
                            setExpandedContent={setExpandedContent}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not specified
                          </Typography>
                        )}
                      </Grid>
                      {/* Resume */}
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Resume
                        </Typography>
                        {(user?.jobPreferences?.resume || user?.resume) ? (
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: 'background.paper',
                              '&:hover': {
                                bgcolor: 'action.hover',
                                borderColor: 'primary.main',
                              }
                            }}
                          >
                            <Stack direction="row" spacing={2} alignItems="center">
                              <AttachFile color="primary" />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" fontWeight="600" noWrap>
                                  {(user.jobPreferences?.resume?.originalName || user.resume?.originalName) || 'Resume.pdf'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Click to view or download
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={1}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => setShowResumeDrawer(true)}
                                
                                  sx={{
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    '&:hover': {
                                      bgcolor: 'primary.dark',
                                       
                                    },
                                    '&:focus': {
                                        bgcolor: 'primary.main',
                                      },
                                      '&:focus-visible': {
                                        bgcolor: 'primary.main',
                                        outline: '2px solid',
                                        outlineColor: 'primary.light',
                                        outlineOffset: 2,
                                      }
                                  }}
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => {
                                    const resumeUrl = user.jobPreferences?.resume?.url || user.resume?.url;
                                    if (resumeUrl) {
                                      window.open(resumeUrl, '_blank');
                                    }
                                  }}
                                  sx={{
                                    bgcolor: 'success.main',
                                    color: 'white',
                                    '&:hover': {
                                      bgcolor: 'success.dark',
                                    },
                                     '&:focus': {
                                        bgcolor: 'primary.main',
                                      },
                                      '&:focus-visible': {
                                        bgcolor: 'primary.main',
                                        outline: '2px solid',
                                        outlineColor: 'primary.light',
                                        outlineOffset: 2,
                                      }
                                  }}
                                >
                                  <OpenInNew fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Stack>
                          </Paper>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No resume uploaded
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Contact Information */}
            <Card elevation={1} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom mb={2}>
                  Contact Information
                </Typography>
                
                {editMode ? (
                  
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 4}}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        name="name"
                        required
                        disabled

                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                      />
                    </Grid>
                    
                    <Grid size={{xs: 12, md: 4}}>
                      <TextField
                        fullWidth
                        label="WhatsApp Number"
                        name="whatsappNumber"
                        required
                        value={formik.values.whatsappNumber}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          formik.setFieldValue('whatsappNumber', value.slice(0, 10));
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.whatsappNumber && Boolean(formik.errors.whatsappNumber)}
                        helperText={formik.touched.whatsappNumber && formik.errors.whatsappNumber}
                        slotProps={{
                          input: {
                            startAdornment: <InputAdornment position="start">+91</InputAdornment>,
                          }
                        }}
                      />
                    </Grid>
                    
                    <Grid size={{xs: 12, md: 4}}>
                      <TextField
                        fullWidth
                        label="Date of Birth"
                        type="date"
                        name="dateOfBirth"
                        required
                        
                        value={formik.values.dateOfBirth}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.dateOfBirth && Boolean(formik.errors.dateOfBirth)}
                        helperText={formik.touched.dateOfBirth && formik.errors.dateOfBirth}
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    </Grid>
                    
                    <Grid size={{xs: 12, md: 4}}>
                      <TextField
                        fullWidth
                        select
                        label="Gender"
                        name="gender"
                        required
                        disabled
                        value={formik.values.gender}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.gender && Boolean(formik.errors.gender)}
                        helperText={formik.touched.gender && formik.errors.gender}
                      >
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </TextField>
                    </Grid>
                    
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Address"
                        name="address"
                        required
                        multiline
                        rows={2}
                        value={formik.values.address}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.address && Boolean(formik.errors.address)}
                        helperText={formik.touched.address && formik.errors.address}
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        label="City"
                        name="city"
                        required
                        value={formik.values.city}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.city && Boolean(formik.errors.city)}
                        helperText={formik.touched.city && formik.errors.city}
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        label="State"
                        name="state"
                        required
                        value={formik.values.state}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.state && Boolean(formik.errors.state)}
                        helperText={formik.touched.state && formik.errors.state}
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        label="Pincode"
                        name="pincode"
                        required
                        value={formik.values.pincode}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.pincode && Boolean(formik.errors.pincode)}
                        helperText={formik.touched.pincode && formik.errors.pincode}
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Designation"
                        name="designation"
                        required
                        value={formik.values.designation}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.designation && Boolean(formik.errors.designation)}
                        helperText={formik.touched.designation && formik.errors.designation}
                        disabled={loadingDesignations}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Full Name
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.name || 'Not specified'}
                      </Typography>
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        WhatsApp Number
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.whatsappNumber ? `+91 ${user.whatsappNumber}` : 'Not specified'}
                      </Typography>
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Date of Birth
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString() : 'Not specified'}
                      </Typography>
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Gender
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.gender || 'Not specified'}
                      </Typography>
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 4  }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Address
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.address || 'Not specified'}
                      </Typography>
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        City
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.city || 'Not specified'}
                      </Typography>
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        State
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.state || 'Not specified'}
                      </Typography>
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Pincode
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.pincode || 'Not specified'}
                      </Typography>
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 4  }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Designation
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {typeof user.designation === 'object' ? user.designation?.name : user.designation || 'Not specified'}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>

            {/* Company Information */}
            <Card elevation={1} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    Current/Previous Company Information
                  </Typography>
                  {/* {editMode && formik.values.isLookingForJob && formik.values.jobPreferences.experienceLevel === 'entry' && (
                    <Chip
                      label="Optional for Entry Level"
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  )} */}
                </Box>

                {editMode ? (
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 6}}>
                      <TextField
                        fullWidth
                        label="Company Name"
                        name="currentCompanyInfo.companyname"
                        required={!(formik.values.isLookingForJob && formik.values.jobPreferences.experienceLevel === 'entry')}
                        value={formik.values.currentCompanyInfo.companyname}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.currentCompanyInfo?.companyname && Boolean(formik.errors.currentCompanyInfo?.companyname)}
                        helperText={formik.touched.currentCompanyInfo?.companyname && formik.errors.currentCompanyInfo?.companyname}
                      />
                    </Grid>

                    <Grid size={{xs: 12, md: 6}}>
                      <TextField
                        fullWidth
                        label="Company URL (Optional)"
                        name="currentCompanyInfo.companyUrl"
                        value={formik.values.currentCompanyInfo.companyUrl}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.currentCompanyInfo?.companyUrl && Boolean(formik.errors.currentCompanyInfo?.companyUrl)}
                        helperText={formik.touched.currentCompanyInfo?.companyUrl && formik.errors.currentCompanyInfo?.companyUrl}
                        placeholder="https://example.com"
                      />
                    </Grid>

                    <Grid size={{xs: 12, md: 6}}>
                      <TextField
                        fullWidth
                        label="Address Line 1"
                        name="currentCompanyInfo.addressLine1"
                        required={!(formik.values.isLookingForJob && formik.values.jobPreferences.experienceLevel === 'entry')}
                        value={formik.values.currentCompanyInfo.addressLine1}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.currentCompanyInfo?.addressLine1 && Boolean(formik.errors.currentCompanyInfo?.addressLine1)}
                        helperText={formik.touched.currentCompanyInfo?.addressLine1 && formik.errors.currentCompanyInfo?.addressLine1}
                      />
                    </Grid>

                    <Grid size={{ xs: 12 , md: 6 }}>
                      <TextField
                        fullWidth
                        label="Address Line 2"
                        name="currentCompanyInfo.addressLine2"
                        value={formik.values.currentCompanyInfo.addressLine2}
                        onChange={formik.handleChange}
                        required={!(formik.values.isLookingForJob && formik.values.jobPreferences.experienceLevel === 'entry')}
                        onBlur={formik.handleBlur}
                        error={formik.touched.currentCompanyInfo?.addressLine2 && Boolean(formik.errors.currentCompanyInfo?.addressLine2)}
                        helperText={formik.touched.currentCompanyInfo?.addressLine2 && formik.errors.currentCompanyInfo?.addressLine2}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        label="City"
                        name="currentCompanyInfo.city"
                        required={!(formik.values.isLookingForJob && formik.values.jobPreferences.experienceLevel === 'entry')}
                        value={formik.values.currentCompanyInfo.city}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.currentCompanyInfo?.city && Boolean(formik.errors.currentCompanyInfo?.city)}
                        helperText={formik.touched.currentCompanyInfo?.city && formik.errors.currentCompanyInfo?.city}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        label="State"
                        name="currentCompanyInfo.state"
                        required={!(formik.values.isLookingForJob && formik.values.jobPreferences.experienceLevel === 'entry')}
                        value={formik.values.currentCompanyInfo.state}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.currentCompanyInfo?.state && Boolean(formik.errors.currentCompanyInfo?.state)}
                        helperText={formik.touched.currentCompanyInfo?.state && formik.errors.currentCompanyInfo?.state}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        label="Pincode"
                        name="currentCompanyInfo.pincode"
                        required={!(formik.values.isLookingForJob && formik.values.jobPreferences.experienceLevel === 'entry')}
                        value={formik.values.currentCompanyInfo.pincode}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.currentCompanyInfo?.pincode && Boolean(formik.errors.currentCompanyInfo?.pincode)}
                        helperText={formik.touched.currentCompanyInfo?.pincode && formik.errors.currentCompanyInfo?.pincode}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6}}>
                      <Typography variant="subtitle2" gutterBottom>
                        Company Name
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.currentCompanyInfo?.companyname || 'Not specified'}
                      </Typography>
                    </Grid>

                    {user.currentCompanyInfo?.companyUrl && (
                      <Grid size={{ xs: 12, md: 6}}>
                        <Typography variant="subtitle2" gutterBottom>
                          Company URL
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <a href={user.currentCompanyInfo.companyUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline' }}>
                            {user.currentCompanyInfo.companyUrl}
                          </a>
                        </Typography>
                      </Grid>
                    )}

                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Address Line 1
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.currentCompanyInfo?.addressLine1 || 'Not specified'}
                      </Typography>
                    </Grid>
                    
                    {user.currentCompanyInfo?.addressLine2 && (
                      <Grid size={{ xs: 12 , md: 6 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Address Line 2
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {user.currentCompanyInfo.addressLine2}
                        </Typography>
                      </Grid>
                    )}
                    
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        City
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.currentCompanyInfo?.city || 'Not specified'}
                      </Typography>
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        State
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.currentCompanyInfo?.state || 'Not specified'}
                      </Typography>
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Pincode
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.currentCompanyInfo?.pincode || 'Not specified'}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>
      {/* About Section */}
           <Card elevation={1} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
               {!editMode &&   <Typography variant="h6" fontWeight="bold" gutterBottom>
                  About
                </Typography>}
                
                {editMode ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    name="aboutMe"
                    required
                    label="About Me"
                    value={formik.values.aboutMe}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.aboutMe && Boolean(formik.errors.aboutMe)}
                    helperText={
                      (formik.touched.aboutMe && formik.errors.aboutMe) ||
                      `${formik.values.aboutMe.length}/500 characters (min: 10, ${500 - formik.values.aboutMe.length} left)`
                    }
                    placeholder="Tell us about yourself, your experience, and what makes you unique..."
                  />
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    {user.aboutMe || 'No description provided'}
                  </Typography>
                )}
              </CardContent>
            </Card> 
            {/* Social Links */}
            <Card elevation={1} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    Social Media Links
                  </Typography>
                  {editMode && (
                    <Button
                      size="small"
                      startIcon={<Add />}
                      onClick={addSocialLink}
                      variant="outlined"
                      disabled={hasEmptyOrInvalidLinks()}
                    >
                      Add Link
                    </Button>
                  )}
                </Box>
                
                {editMode ? (
                  <Stack spacing={2}>
                    {formik.values.socialMediaLinks.map((link, index) => (
                      <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <TextField
                          fullWidth
                          label={`Social Media Link ${index + 1}`}
                          value={link}
                          onChange={(e) => handleSocialLinkChange(index, e.target.value)}
                          error={Boolean(formik.errors.socialMediaLinks?.[index])}
                          helperText={formik.errors.socialMediaLinks?.[index]}
                          placeholder="https://linkedin.com/in/yourprofile"
                        />
                        {formik.values.socialMediaLinks.length > 1 && (
                          <IconButton
                            onClick={() => removeSocialLink(index)}
                            color="error"
                            sx={{ mt: 1 }}
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  user?.socialMediaLinks && user.socialMediaLinks.length > 0 ? (
                    <Stack spacing={2}>
                      {user.socialMediaLinks.map((link, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 2,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: 'action.hover',
                              transform: 'translateY(-1px)',
                              boxShadow: 1,
                            },
                          }}
                          component="a"
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {getSocialIcon(link)}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {getSocialPlatform(link)}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block'
                              }}
                            >
                              {link}
                            </Typography>
                          </Box>
                          <OpenInNew fontSize="small" color="action" />
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No social media links added
                    </Typography>
                  )
                )}
              </CardContent>
            </Card>

            {/* Save Button */}
            {editMode && (
              <Card elevation={1} sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={() => {
                        setEditMode(false);
                        formik.resetForm();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                      onClick={(e) => {
                        e.preventDefault();
                        
                        // Check if user is looking for job but doesn't have a resume
                        const hasResume = formik.values.jobPreferences.resume || 
                                         uploadedResume || 
                                         user?.jobPreferences?.resume || 
                                         user?.resume;
                        
                        if (formik.values.isLookingForJob && !hasResume) {
                          setShowResumeDialog(true);
                          return;
                        }
                        
                        console.log('🔍 FORMIK DEBUG INFO:');
                        console.log('isValid:', formik.isValid);
                        console.log('errors:', formik.errors);
                        console.log('touched:', formik.touched);
                        console.log('values:', formik.values);
                        console.log('isLookingForJob:', formik.values.isLookingForJob);
                        formik.handleSubmit();
                      }}
                      // disabled={loading || !formik.isValid}
                      disabled={loading}
                      sx={{ minWidth: 120 }}
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>

        {/* Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={3}>


          </Stack>
        </Grid>
      </Grid>
      
      {/* Resume Viewer Drawer */}
      <Drawer
        anchor="right"
        open={showResumeDrawer}
        onClose={() => setShowResumeDrawer(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: '100%', md: '40%' },
          },
        }}
      >
        {/* Drawer Header */}
        <Paper
          elevation={2}
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderRadius: 0,
            borderBottom: 1,
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            mt:8,
            zIndex: 10,
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AttachFile color="primary" />
            <Box>
              <Typography variant="h6" fontWeight="600">
                Resume Preview
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(user?.jobPreferences?.resume?.originalName || user?.resume?.originalName) || 'Resume.pdf'}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setShowResumeDrawer(false)}
            sx={{
              bgcolor: 'error.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'error.dark',
              },
              '&:focus': {
                bgcolor: 'error.main',
              },
              '&:focus-visible': {
                bgcolor: 'error.main',
                outline: '2px solid',
                outlineColor: 'error.light',
                outlineOffset: 2,
              }
            }}
          >
            <Close />
          </IconButton>
        </Paper>

        {/* Drawer Content */}
        <Box sx={{ p: 2, height: 'calc(100vh - 80px)', overflow: 'auto' }}>
          {(user?.jobPreferences?.resume || user?.resume) && (() => {
            const resume = user.jobPreferences?.resume || user.resume;
            const resumeUrl = resume?.url;
            const resumeName = resume?.originalName || 'Resume.pdf';
            const isPdf = (resume as any)?.mimeType === 'application/pdf' || resumeName?.toLowerCase().endsWith('.pdf');

            if (!resumeUrl) {
              return (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No resume available
                  </Typography>
                </Paper>
              );
            }

            return isPdf ? (
              <CanvasPdfViewer
                url={resumeUrl}
                fileName={resumeName}
              />
            ) : (
              <Box
                component="iframe"
                src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                  resumeUrl
                )}&embedded=true`}
                sx={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: 1,
                }}
                title={resumeName}
                onError={(e) => {
                  console.error('Error loading document preview:', e);
                }}
              />
            );
          })()}
        </Box>
      </Drawer>

      {/* Resume Required Dialog */}
      <ConfirmDialog
        open={showResumeDialog}
        title="Resume Required"
        message="You have enabled 'Open to work' but haven't uploaded a resume. Would you like to proceed without a resume?"
        confirmText="Proceed Without Resume"
        cancelText="Cancel"
        severity="warning"
        onConfirm={() => {
          setShowResumeDialog(false);
          formik.handleSubmit();
        }}
        onCancel={() => {
          setShowResumeDialog(false);
        }}
      />
    </Container>
  );
};